import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import type { GrButtonSize, GrButtonType, GrButtonVariant } from './button.types';

@Component({
  selector: 'gr-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: contents;' },
  template: `
    <button
      [type]="type()"
      [disabled]="disabled() || loading()"
      [class]="buttonClasses()"
      [attr.aria-label]="ariaLabel() || null"
      [attr.aria-busy]="loading() || null"
      [attr.aria-haspopup]="ariaHasPopup() || null"
      [attr.aria-expanded]="ariaExpanded()"
      [attr.aria-controls]="ariaControls() || null"
      (click)="grClick.emit($event)"
    >
      @if (loading()) {
        <span class="material-icons-outlined gr-button-spinner" aria-hidden="true"
          >autorenew</span
        >
      } @else if (leadingIcon()) {
        <span class="material-icons-outlined gr-button-icon" aria-hidden="true">{{
          leadingIcon()
        }}</span>
      }
      <ng-content />
      @if (!loading() && trailingIcon()) {
        <span class="material-icons-outlined gr-button-icon" aria-hidden="true">{{
          trailingIcon()
        }}</span>
      }
    </button>
  `,
})
export class GrButtonComponent {
  readonly type = input<GrButtonType>('button');
  readonly variant = input<GrButtonVariant>('primary');
  readonly size = input<GrButtonSize>('md');
  readonly fluid = input<boolean>(false);
  readonly disabled = input<boolean>(false);
  readonly loading = input<boolean>(false);
  readonly leadingIcon = input<string>('');
  readonly trailingIcon = input<string>('');
  readonly styleClass = input<string>('');
  readonly ariaLabel = input<string>('');

  // Disclosure state for buttons that open an overlay. These belong on the inner
  // <button>, which consumers can't reach — the host renders `display: contents`.
  readonly ariaHasPopup = input<string>('');
  readonly ariaExpanded = input<boolean | null>(null);
  readonly ariaControls = input<string>('');

  readonly grClick = output<MouseEvent>();

  protected readonly buttonClasses = computed(() => {
    const classes = [
      'gr-button',
      `gr-button--${this.variant()}`,
      `gr-button--${this.size()}`,
      'inline-flex items-center justify-center gap-2 cursor-pointer border-0',
    ];
    if (this.fluid()) classes.push('gr-button--fluid', 'w-full');
    const extra = this.styleClass();
    if (extra) classes.push(extra);
    return classes.join(' ');
  });
}
